import { useState, useEffect } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useUrlParams } from '../../utils/url';
import { apiPost } from '../../utils/api';
import { Card } from '../Card';
import Loading from '../Loading';

type ViewState = 'loading' | 'set_password' | 'success' | 'error';

const SetupAccountPage = () => {
  const { setStorage } = useAuth();
  const params = useUrlParams();
  const orgId = params.org_id;

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const [view, setView] = useState<ViewState>('loading');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setView('error');
      setErrorMessage('No onboarding token provided.');
      return;
    }

    const validateToken = async () => {
      try {
        const response = await apiPost('/api/verify-onboarding', { token });
        const data = await response.json();

        if (response.ok && data.valid) {
          setEmail(data.email);
          setView('set_password');
        } else {
          setView('error');
          setErrorMessage(data.error || 'Invalid onboarding link.');
        }
      } catch {
        setView('error');
        setErrorMessage('Failed to verify onboarding link.');
      }
    };

    validateToken();
  }, [token]);

  const handleSetPassword = async () => {
    setFormError('');

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiPost('/api/setup-account', { token, password });
      const data = await response.json();

      if (response.ok && !data.error) {
        setStorage({
          user: data.user,
          organizations: data.organizations,
          orgMemberships: data.orgMemberships,
          isDemoSession: data.isDemoSession,
        });
        setView('success');
        setTimeout(() => {
          window.location.href = `/${orgId}/general-settings`;
        }, 1500);
      } else {
        setFormError(data.error || 'Failed to set up account.');
      }
    } catch {
      setFormError('Failed to set up account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const leftPanel = (
    <div className="hidden md:flex md:w-1/2 bg-brand-600 items-center justify-center relative p-8">
      <div className="text-white z-10">
        <h2 className="text-3xl font-bold mb-3">Set Up Your Account</h2>
        <p className="text-lg opacity-90">Create a password to get started.</p>
      </div>
      <img
        src="/login.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-90"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    </div>
  );

  if (view === 'loading') {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <div className="flex-1 flex flex-col md:flex-row">
          {leftPanel}
          <div className="w-full md:w-1/2 flex items-center justify-center p-8">
            <Card className="max-w-md w-full p-10 text-center">
              <Loading message="Verifying..." />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'success') {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <div className="flex-1 flex flex-col md:flex-row">
          {leftPanel}
          <div className="w-full md:w-1/2 flex items-center justify-center p-8">
            <Card className="max-w-md w-full p-10 text-center space-y-4">
              <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-900">Account Set Up Successfully</h2>
              <p className="text-gray-600">Redirecting to your dashboard...</p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'error') {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <div className="flex-1 flex flex-col md:flex-row">
          {leftPanel}
          <div className="w-full md:w-1/2 flex items-center justify-center p-8">
            <Card className="max-w-md w-full p-10 text-center space-y-4">
              <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-900">Onboarding Link Invalid</h2>
              <p className="text-gray-600">{errorMessage}</p>
              {orgId && (
                <a
                  href={`/${orgId}/login`}
                  className="inline-flex items-center gap-2 rounded-lg border border-brand-600 bg-white px-4 py-2.5 text-sm font-medium text-brand-600 hover:bg-brand-50 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 mt-4"
                >
                  Go to Login
                </a>
              )}
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="flex-1 flex flex-col md:flex-row">
        {leftPanel}
        <div className="w-full md:w-1/2 flex items-center justify-center p-8">
          <Card className="w-full max-w-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Set Up Your Account</h2>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              {formError && <p className="text-red-500 text-sm">{formError}</p>}
              <button
                onClick={handleSetPassword}
                disabled={isSubmitting || !password || !confirmPassword}
                className="w-full bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
              >
                {isSubmitting ? 'Setting up...' : 'Set Password & Continue'}
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SetupAccountPage;
